import { useState } from "react";
import { FileSpreadsheet, FileText, Loader2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createShareLink, copyToClipboard } from "@/lib/share";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExportBar = ({ inputs, result, schedule }) => {
  const [busy, setBusy] = useState(null);

  const buildPayload = () => ({
    inputs,
    summary: {
      retirementCorpus: result.retirementCorpus,
      firstYearExpense: result.firstYearExpense,
      swr: result.swr,
      depletionAge: result.depletionAge,
      finalCorpus: result.finalCorpus,
      totalWithdrawn: result.totalWithdrawn,
      status: result.status,
      retirementYears: result.retirementYears,
      preReturn: result.preReturn,
    },
    projection: result.projection.map((p) => ({
      age: p.age, year: p.year, open: p.open, withdrawal: p.withdrawal,
      growth: p.growth, close: p.close,
      b1: p.b1 ?? null, b2: p.b2 ?? null, b3: p.b3 ?? null, b4: p.b4 ?? null,
    })),
    schedule: schedule
      ? {
          annual: schedule.annual.map((r) => ({
            phase: r.phase, age: r.age, calYear: r.calYear, monthIndex: null,
            open: r.open, contribution: r.contribution, withdrawal: r.withdrawal,
            growth: r.growth, close: r.close,
          })),
          monthly: schedule.monthly.map((r) => ({
            phase: r.phase, age: r.age, calYear: r.calYear, monthIndex: r.monthIndex,
            open: r.open, contribution: r.contribution, withdrawal: r.withdrawal,
            growth: r.growth, close: r.close,
          })),
        }
      : null,
  });

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await fetch(`${API}/export/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? "My_Finanz_Planner_Retirement_Report.xlsx" : "My_Finanz_Planner_Retirement_Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind === "excel" ? "Excel" : "PDF"} report downloaded`);
    } catch (e) {
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const share = async () => {
    setBusy("share");
    try {
      const url = await createShareLink(inputs, "Shared plan");
      const copied = await copyToClipboard(url);
      if (copied) {
        toast.success("Share link copied to clipboard!", { description: url });
      } else {
        toast.success("Share link created", { description: url, duration: 10000 });
      }
    } catch (e) {
      toast.error("Could not create share link. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="export-bar">
      <Button data-testid="share-link-btn" onClick={share} disabled={busy}
        className="flex-1 h-12 rounded-xl bg-[#1E2B25] hover:bg-[#2b3d34] text-white font-semibold">
        {busy === "share" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
        Share Link
      </Button>
      <Button data-testid="export-excel-btn" onClick={() => download("excel")} disabled={busy}
        variant="outline"
        className="flex-1 h-12 rounded-xl border-[#2D6A4F]/30 bg-white text-[#2D6A4F] hover:bg-[#2D6A4F]/5 font-semibold">
        {busy === "excel" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
        Export Excel
      </Button>
      <Button data-testid="export-pdf-btn" onClick={() => download("pdf")} disabled={busy}
        variant="outline"
        className="flex-1 h-12 rounded-xl border-[#D96B43]/30 bg-white text-[#D96B43] hover:bg-[#D96B43]/5 font-semibold">
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
        Export PDF
      </Button>
    </div>
  );
};
